import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  linkWithPopup,
  linkWithRedirect,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider } from '../../auth/firebase';
import { extractFirebaseAuthError } from '../../auth/firebaseErrors';
import {
  captureFunnelEvent,
  reportGoogleAdsSignupIfNewAccount,
} from '../../services/telemetry';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

const socialButtonClass = `
  flex min-h-[50px] w-full cursor-pointer items-center justify-center gap-2
  rounded-full bg-[#f2f2f2] px-5 py-3 font-medium text-[#3d3d3d]
  transition-transform duration-100
  hover:bg-gray-200
  active:scale-95
  disabled:cursor-not-allowed disabled:opacity-50
`;

/**
 * Google OAuth button for the custom auth screens. Uses Firebase's
 * `signInWithPopup`, which transparently creates an account for first-time
 * OAuth users (OAuth identities arrive email-verified). On success we route to
 * `/post-auth` for idempotent backend provisioning. If the popup is blocked we
 * fall back to `signInWithRedirect` (resolved by the `/sso-callback` handler).
 */
export function SocialAuthButtons({
  onError,
}: {
  onError?: (message: string) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    // Upgrade an anonymous guest in place (`linkWithPopup`) so their UID — and
    // the project they generated as a guest — carries into the new Google
    // account instead of minting a second identity. Non-guests sign in normally.
    const current = auth.currentUser;
    const linkGuest = !!current?.isAnonymous;
    try {
      const cred =
        linkGuest && current
          ? await linkWithPopup(current, googleProvider)
          : await signInWithPopup(auth, googleProvider);
      reportGoogleAdsSignupIfNewAccount(cred, { method: 'google.com' });
      navigate('/post-auth', { replace: true });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      // The Google account already belongs to a real user, so we can't link:
      // sign in with it directly. The guest UID is abandoned here — claiming its
      // project across UIDs is deferred (see GUEST_UPLOAD_NEXT_PHASE.md §2).
      if (
        code === 'auth/credential-already-in-use' &&
        err instanceof FirebaseError
      ) {
        const existing = GoogleAuthProvider.credentialFromError(err);
        if (existing) {
          try {
            // Tag the still-anonymous person before `signInWithCredential` swaps
            // identity to the existing account — lets the guest→signup funnel
            // exclude guests who turned out to already have an account.
            if (linkGuest) {
              captureFunnelEvent('guest_signed_into_existing_account', {
                method: 'google.com',
                anon_uid: current?.uid,
              });
            }
            await signInWithCredential(auth, existing);
            navigate('/post-auth', { replace: true });
            return;
          } catch (signInErr) {
            setBusy(false);
            onError?.(extractFirebaseAuthError(signInErr));
            return;
          }
        }
      }
      if (code === 'auth/popup-blocked') {
        // Popups disabled — fall back to a full-page redirect handshake,
        // mirroring the link-vs-sign-in split above.
        try {
          if (linkGuest && current) {
            await linkWithRedirect(current, googleProvider);
          } else {
            await signInWithRedirect(auth, googleProvider);
          }
          return;
        } catch (redirectErr) {
          setBusy(false);
          onError?.(extractFirebaseAuthError(redirectErr));
          return;
        }
      }
      setBusy(false);
      // Silent on user-cancelled popups.
      if (
        code !== 'auth/popup-closed-by-user' &&
        code !== 'auth/cancelled-popup-request'
      ) {
        onError?.(extractFirebaseAuthError(err));
      }
    }
  };

  return (
    <div className="mt-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-400">Or</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        type="button"
        className={socialButtonClass}
        onClick={start}
        disabled={busy}
      >
        <GoogleIcon />
        {busy ? 'Redirecting…' : 'Continue with Google'}
      </button>
    </div>
  );
}
